import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Contact entity matching the backend data model.
 */
export interface Contact {
  id: string;
  entityType: string;
  entityTypeId?: string;
  contactType: string;
  contactNature: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  secondaryPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  dateOfBirth?: string;
  businessName?: string;
  businessNumber?: string;
  clientCode?: string;
  folderTemplateId?: string | null;
  mailingName?: string;
  salutation?: string;
  deliveryMethod?: string;
  status: 'active' | 'inactive' | 'archived';
  assignedPartnerId?: string;
  assignedManagerId?: string;
  leadSource?: string;
  leadStage?: string;
  leadStageChangedAt?: string;
  leadLossReason?: string;
  convertedAt?: string;
  contactCategory?: string | null;
  tags?: Tag[];
  dynamicFields?: DynamicFieldValue[];
  formFieldValues?: FormFieldValue[];
  pendingReminderCount?: number;
  portalUserId?: string;
  portalInvitationStatus?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Tag {
  id: string;
  name: string;
  code: string;
  description?: string;
  color?: string;
}

export interface DynamicFieldValue {
  fieldDefinitionId: string;
  fieldLabel: string;
  fieldType: string;
  value: unknown;
}

export interface FormFieldValue {
  fieldId: string;
  fieldKey: string;
  value: unknown;
}

export interface ContactsListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  contactType?: string;
  contactTypeNature?: string;
  entityType?: string;
  status?: string;
  tag?: string;
  assignedTo?: string;
  leadStage?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  showAll?: boolean;
}

export interface ContactsListResponse {
  contacts: Contact[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateContactPayload {
  entityTypeId?: string;
  contactType?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  secondaryPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  dateOfBirth?: string;
  businessName?: string;
  businessNumber?: string;
  clientCode?: string;
  folderTemplateId?: string | null;
  mailingName?: string;
  salutation?: string;
  deliveryMethod?: string;
  leadSource?: string;
  assignedPartnerId?: string;
  assignedManagerId?: string;
  dynamicFields?: Record<string, unknown>;
  formData?: Record<string, unknown>;
  tagIds?: string[];
}

export interface UpdateContactPayload extends Partial<CreateContactPayload> {
  status?: 'active' | 'inactive' | 'archived';
  leadStage?: string;
  leadLossReason?: string;
}

export interface ImportResult {
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  errors: Array<{ row: number; field: string; message: string }>;
  reportUrl?: string;
}

export interface LeadStage {
  id: string;
  name: string;
  displayOrder: number;
  reminderDays: number;
}

export interface PipelineResponse {
  stages: Array<{
    stage: LeadStage;
    prospects: Contact[];
    count: number;
  }>;
  totalProspects: number;
}

export interface ConversionMetrics {
  conversionRate: number;
  averageTimeToConvert: number;
  sourceEffectiveness: Record<string, { total: number; converted: number; rate: number }>;
}

export interface ActivityEntry {
  id: string;
  action: string;
  entityType: string;
  description: string;
  userId?: string;
  userName?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const contactsKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactsKeys.all, 'list'] as const,
  list: (params: ContactsListParams) => [...contactsKeys.lists(), params] as const,
  details: () => [...contactsKeys.all, 'detail'] as const,
  detail: (id: string) => [...contactsKeys.details(), id] as const,
  activity: (id: string) => [...contactsKeys.all, 'activity', id] as const,
  pipeline: () => [...contactsKeys.all, 'pipeline'] as const,
  metrics: () => [...contactsKeys.all, 'metrics'] as const,
  tags: () => [...contactsKeys.all, 'tags'] as const,
  stages: () => [...contactsKeys.all, 'stages'] as const,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Converts a camelCase string to snake_case.
 */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Converts a snake_case string to camelCase.
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Transforms a snake_case-keyed object to camelCase keys (shallow).
 */
function toCamelCaseObj<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[toCamelCase(key)] = value;
  }
  return result as T;
}

/**
 * Transforms a camelCase-keyed object to snake_case keys (shallow).
 * Strips keys with undefined/empty-string values.
 */
function toSnakeCasePayload(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const requiredKeys = new Set(['firstName', 'lastName', 'first_name', 'last_name']);
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (value === '' && !requiredKeys.has(key)) continue;
    result[toSnakeCase(key)] = value;
  }
  return result;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch paginated contacts list with search and filters.
 */
export function useContactsList(params: ContactsListParams = {}) {
  return useQuery({
    queryKey: contactsKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination: {
          page: number;
          pageSize: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrevious: boolean;
        };
      }>('/contacts', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
          search: params.search || undefined,
          contact_type: params.contactType || undefined,
          contact_type_nature: params.contactTypeNature || undefined,
          entity_type_id: params.entityType || undefined,
          status: params.status || undefined,
          tag_ids: params.tag || undefined,
          assigned_partner_id: params.assignedTo || undefined,
          lead_stage: params.leadStage || undefined,
          sortField: params.sortBy ? toSnakeCase(params.sortBy) : undefined,
          sortDirection: params.sortDirection || undefined,
          show_all: params.showAll ? 'true' : undefined,
        },
      });
      const raw = response.data;
      const contacts = (raw.data ?? []).map((c) => {
        // Extract tags before camelCase conversion to preserve the array
        const tagsRaw = c.tags as
          | Array<{ id: string; name: string; code: string; description?: string; color?: string }>
          | undefined;
        const contact = toCamelCaseObj<Contact>(c);
        if (tagsRaw && tagsRaw.length > 0) {
          contact.tags = tagsRaw;
        }
        // Ensure pending_reminder_count is a number
        contact.pendingReminderCount = Number(contact.pendingReminderCount) || 0;
        return contact;
      });
      return {
        contacts,
        total: raw.pagination.total,
        page: raw.pagination.page,
        pageSize: raw.pagination.pageSize,
      } as ContactsListResponse;
    },
  });
}

/**
 * Fetch a single contact by ID.
 */
export function useContact(id: string) {
  return useQuery({
    queryKey: contactsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/contacts/${id}`,
      );
      const raw = response.data.data;

      // Extract dynamic_fields before camelCase conversion
      const dynamicFieldsRaw = raw.dynamic_fields as
        | Array<{
            field_definition_id: string;
            field_label: string;
            field_type: string;
            value: unknown;
          }>
        | undefined;

      // Extract form_field_values (new form builder system) before camelCase conversion
      const formFieldValuesRaw = raw.form_field_values as
        | Array<{ field_id: string; field_key: string; value: unknown }>
        | undefined;

      // Extract tags before camelCase conversion
      const tagsRaw = raw.tags as
        | Array<{ id: string; name: string; code: string; description?: string; color?: string }>
        | undefined;

      const contact = toCamelCaseObj<Contact>(raw);

      // Map dynamic fields to the frontend format
      if (dynamicFieldsRaw && dynamicFieldsRaw.length > 0) {
        contact.dynamicFields = dynamicFieldsRaw.map((f) => ({
          fieldDefinitionId: f.field_definition_id,
          fieldLabel: f.field_label,
          fieldType: f.field_type,
          value: f.value,
        }));
      }

      // Map form field values (new system keyed by field_key)
      if (formFieldValuesRaw && formFieldValuesRaw.length > 0) {
        contact.formFieldValues = formFieldValuesRaw.map((f) => ({
          fieldId: f.field_id,
          fieldKey: f.field_key,
          value: f.value,
        }));
      }

      // Preserve tags array
      if (tagsRaw && tagsRaw.length > 0) {
        contact.tags = tagsRaw;
      }

      return contact;
    },
    enabled: !!id,
  });
}

/**
 * Fetch activity timeline for a contact.
 */
export function useContactActivity(id: string) {
  return useQuery({
    queryKey: contactsKeys.activity(id),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Array<Record<string, unknown>>;
      }>(`/contacts/${id}/activity`);
      const raw = response.data.data ?? [];
      return raw.map((entry) => ({
        id: entry.id as string,
        action: entry.action as string,
        entityType: (entry.entity_type as string) || '',
        description: (entry.description as string) || '',
        userId: (entry.user_id as string) || undefined,
        userName: (entry.user_name as string) || undefined,
        createdAt: (entry.created_at as string) || '',
        metadata: (entry.metadata as Record<string, unknown>) || undefined,
      })) as ActivityEntry[];
    },
    enabled: !!id,
  });
}

/**
 * Fetch lead pipeline view (prospects grouped by stage).
 */
export function useLeadPipeline() {
  return useQuery({
    queryKey: contactsKeys.pipeline(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PipelineResponse>>('/contacts', {
        params: { contactType: 'prospect', view: 'pipeline' },
      });
      return response.data.data;
    },
  });
}

/**
 * Fetch conversion metrics for the lead pipeline.
 */
export function useConversionMetrics() {
  return useQuery({
    queryKey: contactsKeys.metrics(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ConversionMetrics>>('/contacts/metrics');
      return response.data.data;
    },
  });
}

/**
 * Fetch all active tags for the tenant (used for tag selection on contacts).
 */
export function useTags() {
  return useQuery({
    queryKey: contactsKeys.tags(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Tag[]>>('/tags', {
        params: { is_active: 'true' },
      });
      return response.data.data;
    },
  });
}

/**
 * Fetch configured lead stages for the tenant.
 */
export function useLeadStages() {
  return useQuery({
    queryKey: contactsKeys.stages(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<LeadStage[]>>('/settings/lead-stages');
      return response.data.data;
    },
  });
}

/**
 * A potential duplicate contact returned by GET /contacts/duplicates.
 * `matchField` indicates whether the email or phone collided.
 */
export interface DuplicateMatch {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  matchField: 'email' | 'phone';
}

/**
 * Checks whether an email and/or phone already belong to another contact in
 * the tenant. Returns the matching contacts (name + which field matched) so the
 * caller can warn the user before creating a duplicate.
 *
 * The /contacts/duplicates endpoint validates `email` as a syntactically valid
 * address, so an invalid/partial email is omitted rather than sent.
 */
export async function checkContactDuplicates(params: {
  email?: string | null;
  phone?: string | null;
}): Promise<DuplicateMatch[]> {
  const query: Record<string, string> = {};
  const email = params.email?.trim();
  const phone = params.phone?.trim();

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (email && EMAIL_RE.test(email)) query.email = email;
  if (phone) query.phone = phone;

  // Nothing to check against — no duplicates by definition.
  if (Object.keys(query).length === 0) return [];

  const response = await apiClient.get<
    ApiResponse<
      Array<{
        id: string;
        first_name: string;
        last_name: string;
        email: string | null;
        phone: string | null;
        match_field: 'email' | 'phone';
      }>
    >
  >('/contacts/duplicates', { params: query });

  return (response.data.data ?? []).map((d) => ({
    id: d.id,
    firstName: d.first_name,
    lastName: d.last_name,
    email: d.email,
    phone: d.phone,
    matchField: d.match_field,
  }));
}

/**
 * Create a new contact.
 */
export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateContactPayload) => {
      const { dynamicFields, formData, tagIds, ...contactFields } = payload;
      const snakeCaseBody = toSnakeCasePayload(contactFields as unknown as Record<string, unknown>);

      // Include dynamic_fields if present
      if (dynamicFields && Object.keys(dynamicFields).length > 0) {
        snakeCaseBody.dynamic_fields = dynamicFields;
      }

      // Include form_data JSONB if present
      if (formData && Object.keys(formData).length > 0) {
        snakeCaseBody.form_data = formData;
      }

      // Include tag_ids if present
      if (tagIds && tagIds.length > 0) {
        snakeCaseBody.tag_ids = tagIds;
      }

      const response = await apiClient.post<ApiResponse<Contact>>('/contacts', snakeCaseBody);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.pipeline() });
    },
  });
}

/**
 * Update an existing contact.
 */
export function useUpdateContact(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateContactPayload) => {
      const { dynamicFields, formData, tagIds, ...contactFields } = payload;
      const snakeCaseBody = toSnakeCasePayload(contactFields as unknown as Record<string, unknown>);

      // Include dynamic_fields if present
      if (dynamicFields && Object.keys(dynamicFields).length > 0) {
        snakeCaseBody.dynamic_fields = dynamicFields;
      }

      // Include form_data JSONB if present
      if (formData && Object.keys(formData).length > 0) {
        snakeCaseBody.form_data = formData;
      }

      // Always include tag_ids on update to sync tags
      snakeCaseBody.tag_ids = tagIds ?? [];

      const response = await apiClient.put<ApiResponse<Contact>>(`/contacts/${id}`, snakeCaseBody);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.pipeline() });
    },
  });
}

/**
 * Delete (archive) a contact.
 */
export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.pipeline() });
    },
  });
}

/**
 * Convert a prospect to a client.
 */
export function useConvertContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<Contact>>(`/contacts/${id}/convert`);
      return response.data.data;
    },
    onSuccess: (data, id) => {
      // Set the contact data directly from the convert response to avoid a re-fetch
      // that may fail due to data role filters excluding the newly-converted contact_type.
      queryClient.setQueryData(contactsKeys.detail(id), data);
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.pipeline() });
    },
  });
}

/**
 * Update lead stage (drag-and-drop in pipeline).
 */
export function useUpdateLeadStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactId,
      stage,
      lossReason,
    }: {
      contactId: string;
      stage: string;
      lossReason?: string;
    }) => {
      const response = await apiClient.patch<ApiResponse<Contact>>(`/contacts/${contactId}`, {
        lead_stage: stage,
        lead_loss_reason: lossReason,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.pipeline() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
    },
  });
}

/**
 * Bulk assign prospects to a staff member.
 */
export function useBulkAssign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contactIds,
      assigneeId,
    }: {
      contactIds: string[];
      assigneeId: string;
    }) => {
      const response = await apiClient.post<ApiResponse<{ updated: number }>>(
        '/contacts/bulk-assign',
        { contactIds, assigneeId },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contactsKeys.pipeline() });
    },
  });
}

/**
 * Import contacts from CSV file.
 */
export function useImportContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ApiResponse<ImportResult>>(
        '/contacts/import',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.lists() });
    },
  });
}

/**
 * Add tags to a contact.
 */
export function useAddTags(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagIds: string[]) => {
      const response = await apiClient.post<ApiResponse<Contact>>(`/contacts/${contactId}/tags`, {
        tagIds,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.detail(contactId) });
    },
  });
}

/**
 * Remove a tag from a contact.
 */
export function useRemoveTag(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tagId: string) => {
      await apiClient.delete(`/contacts/${contactId}/tags/${tagId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.detail(contactId) });
    },
  });
}
